import { PublicHoliday } from './../../../shared/types/generated.types';
import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, interval, map, Observable, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventBusService, EventType } from 'src/app/shared/services/event-bus.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CalendarEvent } from '../../../shared/types/calendar/calendar-event.type';
import { CalendarEventType, Permission } from '../../../shared/enums/generated.enums';
import { ApiService } from '../../../shared/services/api/api.service';
import { DateHelper } from '../../../shared/helpers/date.helper';
import { DateRange } from '../../../shared/types/date-range.type';

interface CachedData<T> {
  from: Date;
  to: Date;
  data: T[];
}

@Injectable({
  providedIn: 'root',
})
export class CalendarEventService {
  private ongoingMaintenanceWindowsSubject$ = new BehaviorSubject<CalendarEvent[]>([]);
  private eventCaches = new Map<string, Map<string, CachedData<CalendarEvent>>>();
  private holidaysCache = new Map<string, CachedData<PublicHoliday>>();
  private defaultCalendarId: string;

  get ongoingMaintenanceWindowsCount$(): Observable<number> {
    return this.ongoingMaintenanceWindowsSubject$.pipe(
      map(events => events.length),
      distinctUntilChanged(),
    );
  }

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    eventBusService: EventBusService,
  ) {
    eventBusService.on(EventType.OrganizationChanged).subscribe(() => void this.refresh());
    if (this.authService.hasPermission(Permission.Orchestration)) {
      void this.checkOngoingMaintenanceEvents();
      interval(60000)
        .pipe(
          takeUntilDestroyed(),
          switchMap(async () => await this.checkOngoingMaintenanceEvents()),
        )
        .subscribe();
    }
  }

  async refresh(): Promise<void> {
    this.ongoingMaintenanceWindowsSubject$.next([]);
    if (this.authService.hasPermission(Permission.Orchestration)) {
      await this.checkOngoingMaintenanceEvents();
    }
  }

  async getDefaultCalendarId(): Promise<string> {
    if (this.defaultCalendarId != null) {
      return this.defaultCalendarId;
    }

    try {
      const calendars = await this.apiService.calendar.getCalendars();
      this.defaultCalendarId = calendars.find(c => c.name?.toLowerCase() === 'default')?.id ?? calendars[0]?.id;
      return this.defaultCalendarId;
    } catch (error) {
      console.error('Error initializing calendar:', error);
      throw error;
    }
  }

  private async checkOngoingMaintenanceEvents(): Promise<void> {
    try {
      const calendarId = await this.getDefaultCalendarId();
      if (calendarId == null) {
        return;
      }

      // Get today's events
      const start = DateHelper.startOfToday();
      const dateRange = new DateRange(start, DateHelper.addDays(start, 1));
      const todayEvents = await this.getCalendarEvents(calendarId, dateRange);

      // Filter for ongoing maintenance events
      const now = new Date();
      const ongoingMaintenanceEvents = todayEvents.filter((event: CalendarEvent) =>
        event.type === CalendarEventType.MaintenanceWindow &&
        event.start <= now &&
        (event.end == null || event.end >= now),
      );

      this.ongoingMaintenanceWindowsSubject$.next(ongoingMaintenanceEvents);
    } catch (error) {
      console.error('Error checking ongoing maintenance events:', error);
      this.ongoingMaintenanceWindowsSubject$.next([]);
    }
  }

  async getCalendarEvents(calendarId: string, dateRange: DateRange, tenantId?: string): Promise<CalendarEvent[]> {
    if (!calendarId || !dateRange) {
      return [];
    }

    try {
      if (!this.eventCaches.has(calendarId)) {
        this.eventCaches.set(calendarId, new Map<string, CachedData<CalendarEvent>>());
      }
      const cache = this.eventCaches.get(calendarId);

      return await this.getCachedData(cache, `${tenantId ?? 'all'}`, dateRange,
        () => this.apiService.calendar.getEvents(calendarId, dateRange.start, dateRange.end, tenantId),
        item => `${item.id}${item.start.getTime()}${item.end?.getTime()}`,
      );
    } catch (error) {
      console.error(`Error fetching calendar events for ${calendarId}:`, error);
      return [];
    }
  }

  async getPublicHolidays(dateRange: DateRange): Promise<PublicHoliday[]> {
    try {
      return await this.getCachedData(this.holidaysCache, 'all', dateRange,
        () => this.apiService.calendar.getPublicHolidays(dateRange.start, dateRange.end),
        item => `${item.countryCode}${item.englishName}${item.date.getTime()}`,
      );
    } catch (error) {
      console.error('Error fetching public holidays:', error);
      return [];
    }
  }

  private async getCachedData<T>(cache: Map<string, CachedData<T>>, cacheKey: string, dateRange: DateRange, getData: () => Promise<T[]>, getDataKey: (item: T) => string): Promise<T[]> {
    if (!cache.has(cacheKey)) {
      const data = {
        from: dateRange.start,
        to: dateRange.end,
        data: await getData(),
      };
      cache.set(cacheKey, data);
    }

    const cachedData = cache.get(cacheKey);
    if (cachedData.from <= dateRange.start && cachedData.to >= dateRange.end) {
      return cachedData.data;
    }

    const data = await getData();
    const mergedData = mergeData(cachedData.data, data, getDataKey);
    cache.set(cacheKey, {
      from: DateHelper.min(cachedData.from, dateRange.start),
      to: DateHelper.max(cachedData.to, dateRange.end),
      data: mergedData,
    });
    return mergedData;

    function mergeData(oldData: T[], newData: T[], getDataKey: (item: T) => string): T[] {
      const map = new Map<string, T>();
      oldData.forEach(event => map.set(getDataKey(event), event));
      newData.forEach(event => map.set(getDataKey(event), event));
      return Array.from(map.values());
    }
  }

  clearEventCache(calendarId?: string): void {
    if (calendarId && this.eventCaches.has(calendarId)) {
      this.eventCaches.get(calendarId).clear();
    } else {
      this.eventCaches.clear();
    }

    this.holidaysCache.clear();
  }

  async getCalendarEventById(eventId: string, startDate: Date): Promise<CalendarEvent> {
    return await this.apiService.calendar.getEvent(eventId, startDate);
  }
}
